import { useState, useEffect, useRef, useCallback, useMemo, createContext } from 'react';
import { Stack, Skeleton, Typography } from '@mui/material';
import { Responsive, WidthProvider, Layouts } from 'react-grid-layout';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { debounce } from 'lodash';
import { ChartItem } from './ChartItemCom';
import { getZoomLevel, hasZoomChanged, addZoomChangeListener } from 'utils/zoomUtils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { CHART_GRID_CONFIG } from 'constant/ChartViewConstant';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

type ChartDisplayPreview = {
  values: AutoCompleteItem[];
  layouts: Layouts;
  generalFilter: {
    memberIds: string[];
    cubeAttributeId: string;
    insightViewAttributeIds?: string[];
    accumulationType: string;
  };
  isLoading?: boolean;
};

// Height scale factor removed — layouts are now used as-is from the editor.
// Previously this was shrinking all chart heights by 25-50%, causing charts
// to display smaller than what was configured in the editor.

type ChartLayoutContextType = {
  rowHeight: number;
};
export const ChartLayoutContext = createContext<ChartLayoutContextType | undefined>(undefined);

const getMarginForBreakpoint = (bp: string): [number, number] => {
  const base = CHART_GRID_CONFIG.MARGIN;
  switch (bp) {
    case 'lg':
    case 'md':
      return base;
    case 'sm':
      return [8, 8];
    default:
      return [6, 6];
  }
};

// ── 4B: Loading skeleton grid ──
function ChartGridSkeleton() {
  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: '#f8f9fb',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction='row' gap={1.5} height='50%' mb={1.5}>
        <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
        <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
      </Stack>
      <Stack direction='row' gap={1.5} height='50%'>
        <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
        <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
      </Stack>
    </Stack>
  );
}

export function ChartDisplayLayout(props: Readonly<ChartDisplayPreview>) {
  const { values, layouts: chartLayouts, generalFilter, isLoading = false } = props;
  const [layouts, setLayouts] = useState<Layouts>({ lg: [], md: [], sm: [], xs: [], xxs: [] });
  const [charts, setCharts] = useState<AutoCompleteItem[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [rowHeight, setRowHeight] = useState(30);
  const [zoomLevel, setZoomLevel] = useState(getZoomLevel());
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');

  const breakpoints = CHART_GRID_CONFIG.BREAKPOINTS;
  const cols = CHART_GRID_CONFIG.COLS;

  const updateRowHeight = useCallback(() => {
    if (!containerRef.current) return;
    const { offsetHeight } = containerRef.current;
    const totalRows = CHART_GRID_CONFIG.TOTAL_ROWS;
    const newRowHeight = Math.max(1, Math.floor(offsetHeight / totalRows));
    setRowHeight(newRowHeight);
  }, []);

  const debouncedUpdateRowHeight = useMemo(() => debounce(updateRowHeight, 250), [updateRowHeight]);

  const handleZoomAndResize = useCallback(() => {
    const newZoomLevel = getZoomLevel();
    if (hasZoomChanged(zoomLevel, newZoomLevel)) {
      setZoomLevel(newZoomLevel);
      setTimeout(() => {
        debouncedUpdateRowHeight();
      }, 500);
    } else {
      debouncedUpdateRowHeight();
    }
  }, [zoomLevel, debouncedUpdateRowHeight]);

  useEffect(() => {
    const cleanup = addZoomChangeListener(handleZoomAndResize);
    handleZoomAndResize();

    return () => {
      cleanup();
      debouncedUpdateRowHeight.cancel?.();
    };
  }, [handleZoomAndResize, debouncedUpdateRowHeight]);

  useEffect(() => {
    if (chartLayouts && Object.keys(chartLayouts).length > 0) {
      // Only use the lg layout — let react-grid-layout handle
      // responsive reflow for smaller breakpoints at runtime.
      // This matches the editor which only persists lg.
      const lgLayout = chartLayouts.lg || [];
      setLayouts({ lg: lgLayout });
    }
    if (values && values.length > 0) {
      const layoutIds = new Set();
      // Use lg layout for filtering since it's the source of truth
      const lgItems = chartLayouts?.lg || [];
      lgItems.forEach((item) => {
        layoutIds.add(item.i);
      });

      const filteredCharts = values.filter((chart) => layoutIds.has(chart.label));
      setCharts(filteredCharts);
    }
  }, [values, chartLayouts]);

  const margin = getMarginForBreakpoint(currentBreakpoint);

  return (
    <Stack flex={1} minHeight={0} direction='row' px={2} py={1} gap={2} ref={containerRef}>
      <ChartLayoutContext.Provider value={{ rowHeight }}>
        <Stack
          width='100%'
          flex={1}
          minHeight={0}
          sx={{
            bgcolor: '#f8f9fb',
            borderRadius: 2,
            border: '1px solid #eef0f4',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* ── Loading skeleton ── */}
          {isLoading && <ChartGridSkeleton />}

          {/* ── 3A: Empty state ── */}
          {!isLoading && charts.length === 0 && (
            <Stack height='100%' alignItems='center' justifyContent='center' gap={1} p={4}>
              <TrendingUpIcon sx={{ fontSize: 48, color: '#ddd' }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#999' }}>No charts to display</Typography>
              <Typography sx={{ fontSize: 12, color: '#bbb', textAlign: 'center', maxWidth: 300 }}>
                This chart view has no configured charts, or the layout data could not be loaded.
              </Typography>
            </Stack>
          )}

          {/* ── Chart grid ── */}
          {!isLoading && charts.length > 0 && (
            <ResponsiveGridLayout
              className='layout'
              style={{
                width: '100%',
                minHeight: '100%',
              }}
              layouts={layouts}
              breakpoints={breakpoints}
              cols={cols}
              isResizable={false}
              isDraggable={false}
              isDroppable={false}
              rowHeight={rowHeight}
              margin={margin}
              containerPadding={CHART_GRID_CONFIG.CONTAINER_PADDING}
              onBreakpointChange={(bp) => setCurrentBreakpoint(bp)}
            >
              {charts.map((item) => (
                <div
                  key={item.label}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ChartItem chartItemId={item.value?.toString()} key={item.label} {...generalFilter} />
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </Stack>
      </ChartLayoutContext.Provider>
    </Stack>
  );
}
